import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { CartProvider } from "@/contexts/CartContext";
import { Toaster } from 'sonner';
import ProtectedRoute from "@/components/ProtectedRoute";
import { DASHBOARD_ROUTE_ELEMENTS } from "@/soluqr/tiers/DashboardTierRoutes";
import MenuWelcomeRedirect from "@/pages/menu/MenuWelcomeRedirect";
import MenuCatalogPage from "@/pages/menu/MenuCatalogPage";
import CheckoutPage from "@/pages/menu/CheckoutPage";
import AssistantPage from "@/pages/menu/AssistantPage";
import { lazyPageComponent } from "@/routes/lazyPage";
import { shouldAttemptHostnameTenantResolve } from "@/lib/tenant/resolveTenantBusiness";
import CookieConsentBanner from "@/components/legal/CookieConsentBanner";

// Lazy — auth, landings SEO, marketing, SoluGo, panel, perfil/asistente menú
const LoginPage = lazyPageComponent(() => import("@/pages/LoginPage"));
const RegisterPage = lazyPageComponent(() => import("@/pages/RegisterPage"));
const AcceptInvitePage = lazyPageComponent(() => import("@/pages/AcceptInvitePage"));
const DashboardLayout = lazyPageComponent(() => import("@/components/DashboardLayout"));
const CustomerProfilePage = lazyPageComponent(() => import("@/pages/menu/CustomerProfilePage"));
const LandingPage = lazyPageComponent(() => import("@/pages/LandingPage"));
const PricingPage = lazyPageComponent(() => import("@/pages/PricingPage"));
const CapacitacionPage = lazyPageComponent(() => import("@/pages/CapacitacionPage"));
const CapacitacionDemoPanelPage = lazyPageComponent(() => import("@/pages/CapacitacionDemoPanelPage"));
const CapacitacionSoluGoPage = lazyPageComponent(() => import("@/pages/CapacitacionSoluGoPage"));
const CapacitacionAbrirLocalPage = lazyPageComponent(() => import("@/pages/CapacitacionAbrirLocalPage"));
const StatusPage = lazyPageComponent(() => import("@/pages/StatusPage"));
const TermsPage = lazyPageComponent(() => import("@/pages/TermsPage"));
const PrivacyPage = lazyPageComponent(() => import("@/pages/PrivacyPage"));
const SeoLandingPage = lazyPageComponent(() => import("@/pages/SeoLandingPage"));
const CustomerStoriesPage = lazyPageComponent(() => import("@/pages/CustomerStoriesPage"));
const CustomerStoryPage = lazyPageComponent(() => import("@/pages/CustomerStoryPage"));
const ColinaHomePage = lazyPageComponent(() => import("@/pages/ColinaHomePage"));
const ColinaVendorPage = lazyPageComponent(() => import("@/pages/ColinaVendorPage"));
const ColinaPublicidadPage = lazyPageComponent(() => import("@/pages/ColinaPublicidadPage"));
const SoluGoLoginPage = lazyPageComponent(() => import("@/pages/SoluGoLoginPage"));
const SoluGoPublicPage = lazyPageComponent(() => import("@/pages/SoluGoPublicPage"));
const SoluGoRiderPage = lazyPageComponent(() => import("@/pages/SoluGoRiderPage"));
const SoluGoTrackingPage = lazyPageComponent(() => import("@/pages/SoluGoTrackingPage"));
const SoluGoLocalLandingPage = lazyPageComponent(() => import("@/pages/SoluGoLocalLandingPage"));
const SoluGoExternalLocalLoginPage = lazyPageComponent(() => import("@/pages/SoluGoExternalLocalLoginPage"));
const SoluGoExternalLocalDashboardPage = lazyPageComponent(() => import("@/pages/SoluGoExternalLocalDashboardPage"));
const OwnDeliveryDriverPage = lazyPageComponent(() => import("@/pages/OwnDeliveryDriverPage"));
const OrderStatusPage = lazyPageComponent(() => import("@/pages/OrderStatusPage"));
const OnboardingWizard = lazyPageComponent(() => import("@/pages/OnboardingWizard"));
const AdminPage = lazyPageComponent(() => import("@/pages/AdminPage"));
const SoluqrAdPage = lazyPageComponent(() => import("@/pages/SoluqrAdPage"));

/** En dominio tenant / custom host, la raíz abre el menú público. En soluqr.cl principal, landing comercial. */
function PublicMenuRootOrLogin() {
  const hostname =
    typeof window !== "undefined"
      ? (window.location.hostname || "").trim().toLowerCase()
      : "";
  if (shouldAttemptHostnameTenantResolve(hostname)) {
    return <Navigate to="/catalog" replace />;
  }
  return <LandingPage />;
}

function App() {
  return (
    <AuthProvider>
      <CartProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<PublicMenuRootOrLogin />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/accept-invite" element={<AcceptInvitePage />} />
            <Route path="/order-status/:businessSlug/:shortId" element={<OrderStatusPage />} />
            <Route path="/order-status/:orderId" element={<OrderStatusPage />} />
            <Route path="/colina" element={<ColinaHomePage />} />
            <Route path="/colina/vendedor" element={<ColinaVendorPage />} />
            <Route path="/colina/publicidad" element={<ColinaPublicidadPage />} />
            <Route path="/solugo" element={<SoluGoPublicPage />} />
            <Route path="/solugo/login" element={<SoluGoLoginPage />} />
            <Route path="/solugo/rider" element={<SoluGoRiderPage />} />
            <Route path="/own-delivery/driver" element={<OwnDeliveryDriverPage />} />
            <Route path="/solugo/track/:requestId" element={<SoluGoTrackingPage />} />
            <Route path="/solugo/local" element={<SoluGoLocalLandingPage />} />
            <Route path="/solugo/local/login" element={<SoluGoExternalLocalLoginPage />} />
            <Route path="/solugo/local/dashboard" element={<SoluGoExternalLocalDashboardPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/capacitacion" element={<CapacitacionPage />} />
            <Route path="/capacitacion/panel" element={<CapacitacionDemoPanelPage />} />
            <Route path="/capacitacion/solugo" element={<CapacitacionSoluGoPage />} />
            <Route path="/capacitacion/abrir-local" element={<CapacitacionAbrirLocalPage />} />
            <Route path="/status" element={<StatusPage />} />
            <Route path="/terminos" element={<TermsPage />} />
            <Route path="/privacidad" element={<PrivacyPage />} />
            <Route path="/menu-qr-restaurantes" element={<SeoLandingPage slug="menu-qr-restaurantes" />} />
            <Route path="/menu-digital-restaurantes" element={<SeoLandingPage slug="menu-digital-restaurantes" />} />
            <Route path="/pedidos-whatsapp-restaurantes" element={<SeoLandingPage slug="pedidos-whatsapp-restaurantes" />} />
            <Route path="/carta-digital-restaurante" element={<SeoLandingPage slug="carta-digital-restaurante" />} />
            <Route path="/asistente-ia-restaurantes" element={<SeoLandingPage slug="asistente-ia-restaurantes" />} />
            <Route path="/menu-qr-cafeterias" element={<SeoLandingPage slug="menu-qr-cafeterias" />} />
            <Route path="/menu-qr-food-trucks" element={<SeoLandingPage slug="menu-qr-food-trucks" />} />
            <Route path="/menu-qr-bares" element={<SeoLandingPage slug="menu-qr-bares" />} />
            <Route path="/menu-qr-pizzerias" element={<SeoLandingPage slug="menu-qr-pizzerias" />} />
            <Route path="/menu-qr-hamburgueserias" element={<SeoLandingPage slug="menu-qr-hamburgueserias" />} />
            <Route path="/menu-qr-dark-kitchens" element={<SeoLandingPage slug="menu-qr-dark-kitchens" />} />
            <Route path="/menu-digital-restaurantes-pequenos" element={<SeoLandingPage slug="menu-digital-restaurantes-pequenos" />} />
            <Route path="/sistema-pedidos-delivery" element={<SeoLandingPage slug="sistema-pedidos-delivery" />} />
            <Route path="/soluqr-vs-carta-pdf" element={<SeoLandingPage slug="soluqr-vs-carta-pdf" />} />
            <Route path="/soluqr-vs-menu-impreso" element={<SeoLandingPage slug="soluqr-vs-menu-impreso" />} />
            <Route path="/soluqr-vs-linktree" element={<SeoLandingPage slug="soluqr-vs-linktree" />} />
            <Route path="/soluqr-vs-whatsapp-manual" element={<SeoLandingPage slug="soluqr-vs-whatsapp-manual" />} />
            <Route path="/mejor-menu-qr-restaurantes-chile" element={<SeoLandingPage slug="mejor-menu-qr-restaurantes-chile" />} />

            <Route path="/restaurantes-que-usan-soluqr" element={<CustomerStoriesPage />} />
            <Route path="/caso-bigmati-grill" element={<CustomerStoryPage slug="caso-bigmati-grill" />} />
            <Route path="/caso-chicken-pollo-chicureo" element={<CustomerStoryPage slug="caso-chicken-pollo-chicureo" />} />

            <Route path="/dashboard" element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }>
              {DASHBOARD_ROUTE_ELEMENTS.map(({ segment, element }) => (
                <Route key={segment || 'index'} path={segment} element={element} />
              ))}
              <Route path="live-orders" element={<Navigate to="/dashboard/orders-live" replace />} />
              <Route path="cashier" element={<Navigate to="/dashboard/pos" replace />} />
            </Route>

            {/* Onboarding Wizard */}
            <Route path="/onboarding" element={
              <ProtectedRoute>
                <OnboardingWizard />
              </ProtectedRoute>
            } />

            {/* Admin Route */}
            <Route path="/admin" element={
              <ProtectedRoute>
                <AdminPage />
              </ProtectedRoute>
            } />

            {/* soluqr ad — PWA admin global */}
            <Route path="/soluqr-ad" element={<SoluqrAdPage />} />
            <Route path="/soluqr-ad/*" element={<SoluqrAdPage />} />

            {/* Public Menu — mismo host sin /menu/:businessId (eager: flujo crítico) */}
            <Route path="/catalog" element={<MenuCatalogPage />} />
            <Route path="/checkout" element={<CheckoutPage />} />
            <Route path="/assistant" element={<AssistantPage />} />
            <Route path="/profile" element={<CustomerProfilePage />} />

            {/* Public Menu Routes — /menu/:businessId (compat, eager) */}
            <Route path="/menu" element={<MenuWelcomeRedirect />} />
            <Route path="/menu/:businessId" element={<MenuCatalogPage />} />
            <Route path="/menu/:businessId/catalog" element={<MenuCatalogPage />} />
            <Route path="/menu/:businessId/checkout" element={<CheckoutPage />} />
            <Route path="/menu/:businessId/assistant" element={<AssistantPage />} />
            <Route path="/menu/:businessId/profile" element={<CustomerProfilePage />} />
          </Routes>
          <Toaster position="top-center" richColors />
          <CookieConsentBanner />
        </BrowserRouter>
      </CartProvider>
    </AuthProvider>
  );
}

export default App;
